import { api, downloadFile } from './api.js';
import { canUseCashboxRoute, type CashboxRouteAction } from '@mnp/shared';
import type { Role } from './role.ts';

// ---- Types ----

export type OperationType = 'cash' | 'transfer' | 'europe' | 'debt';
export type OperationDirection = 'income' | 'expense';
export type PaymentMethod = 'transfer' | 'terminal' | 'card';
export type CurrencyCode = 'EUR' | 'USD' | 'RUB' | 'UAH' | 'USDT';

export type CurrencyAmount = {
  code: CurrencyCode;
  amount: number;
  rate?: number;
  gel?: number;
};

export type AmoTrackStatus = {
  status: 'ok' | 'error' | 'pending';
  dealId?: string;
  error?: string;
  syncedAt?: string;
};

export type CashboxOperation = {
  id: string;
  cashboxId: string;
  shiftId: string | null;
  type: OperationType;
  direction: OperationDirection;
  opType: string;
  amountGel: number;
  currencies: CurrencyAmount[];
  paymentMethod: PaymentMethod | null;
  account: string | null;
  tracks: string[];
  cashier: string | null;
  payerName: string | null;
  comment: string | null;
  operatedAt: string;
  receivedAt: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  amoCurrentBudget: number | null;
  amoLeadUrl: string | null;
  amoDealId: string | null;
  amoStatus: Record<string, AmoTrackStatus> | null;
  amoCompareResult: string | null;
};

export type CashboxShift = {
  id: string;
  cashboxId: string;
  openedBy: string;
  openedAt: string;
  openingAmount: number;
  closedBy: string | null;
  closedAt: string | null;
  closingAmount: number | null;
  expectedAmount: number | null;
  discrepancy: number | null;
  comment: string | null;
};

export type CashboxAuditEntry = {
  id: string;
  cashboxId: string;
  actorChatId: string;
  event:
    | 'create'
    | 'update'
    | 'soft_delete'
    | 'restore'
    | 'hard_delete'
    | 'open_shift'
    | 'close_shift'
    | 'import'
    | 'settings_update'
    | 'amo_resync';
  entityType: 'operation' | 'shift' | 'import' | 'settings';
  entityId: string;
  field: string | null;
  oldValue: unknown;
  newValue: unknown;
  source: 'bot' | 'admin' | 'api' | 'import';
  comment: string | null;
  createdAt: string;
};

export type CashboxRate = {
  currency: string;
  rate: number;
  source: string;
  fetchedAt: string;
};

export type CashboxSettings = {
  id: number;
  amoBaseUrl: string | null;
  amoFieldPaymentMethod: number;
  amoFieldTrackAmount: number;
  amoEnumCash: number | null;
  amoEnumTransfer: number | null;
  amoEnumTerminal: number | null;
  amoEnabled: boolean;
  amoAllowedPipelineIds: number[];
  // Flex AMO mapping override (воронка/этапы/поля/enum'ы). null = всё по дефолту.
  amoFlexConfig: Record<string, unknown> | null;
  // Flex-CIS AMO mapping override (коридор Грузия↔СНГ). null = всё по дефолту =
  // dormant (синк не зовётся, пока не задана воронка). Без paymentEnums.
  amoFlexCisConfig: Record<string, unknown> | null;
  telegramChannelChatId: string | null;
  dailyReportTime: string;
  dailyReportEnabled: boolean;
  notifyEachOperation: boolean;
  archiveRetentionDays: number;
  ratesAutoRefresh: boolean;
};

export type DashboardData = {
  cashIncome: number;
  cashExpense: number;
  transferIncome: number;
  transferExpense: number;
  europeIncome: number;
  europeExpense: number;
  debtIncome: number;
  debtExpense: number;
  balance: number;
  operationCount: number;
  period: { from: string; to: string };
};

export type GroupedReportRow = {
  label: string;
  income: number;
  expense: number;
  net: number;
  count: number;
};

type DashboardApiData = DashboardData & {
  kpis?: Record<string, number>;
  recent?: CashboxOperation[];
  // true = сервер упёрся в предохранитель полной выборки (50 000 строк):
  // KPI посчитаны не по всем операциям — надо сузить период.
  truncated?: boolean;
};

type GroupedReportApiData = ListResponse<GroupedReportRow> & {
  by: string;
  truncated?: boolean;
};

export type ListResponse<T> = {
  items: T[];
  total: number;
  nextCursor: string | null;
};

export type ImportResult = {
  created: number;
  skipped: number;
  updated: number;
  errors: Array<{ rowIndex: number; reason: string }>;
};

export type CreateOperationInput = {
  cashboxId?: string;
  type: OperationType;
  direction: OperationDirection;
  opType: string;
  amountGel: number;
  currencies?: Array<{ code: CurrencyCode; amount: number; rate?: number }>;
  paymentMethod?: PaymentMethod;
  account?: string;
  tracks: string[];
  cashier?: number | string;
  payerName?: string;
  comment?: string;
  operatedAt: string;
};

export type CreateOperationDealInput = Omit<CreateOperationInput, 'cashboxId' | 'cashier'>;

export type CreateOperationsBatchInput = {
  cashboxId?: string;
  shiftId?: string;
  deals: CreateOperationDealInput[];
};

export type CreateOperationResponse = CashboxOperation | { operationIds: string[] };

export type UpdateOperationInput = {
  amountGel?: number;
  opType?: string;
  direction?: OperationDirection; // модалка direction НЕ шлёт: владелец — сервер, меняет только вместе со сменой opType
  comment?: string | null;
  tracks?: string[];
  paymentMethod?: PaymentMethod;
  account?: string;
  payerName?: string;
  currencies?: Array<{ code: CurrencyCode; amount: number; rate?: number }>;
  expectedUpdatedAt: string;
};

// ---- RBAC ----

// Единая cashbox-матрица живёт в packages/shared/src/rbac (CASHBOX_ROUTE_RBAC)
// — та же таблица, которой роуты API гейтят запросы. Локальная копия-зеркало
// удалена (rbac-single-source); заодно сюда автоматически пришёл action
// `amo_config` (management-only), которого в отставшей копии не было.
type CashboxAction = CashboxRouteAction;

export function canDoCashbox(role: Role, action: CashboxAction): boolean {
  return canUseCashboxRoute(role, action);
}

// ---- API helpers ----

function buildQuery(params?: Record<string, string | undefined>): string {
  if (!params) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') p.set(k, v);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

// ---- API client ----

export const cashboxApi = {
  // Operations
  getOperations: (params?: Record<string, string | undefined>) =>
    api<ListResponse<CashboxOperation>>(`/admin/cashbox/operations${buildQuery(params)}`),

  getOperation: (id: string) =>
    api<CashboxOperation>(`/admin/cashbox/operations/${id}`),

  createOperation: (data: CreateOperationInput | CreateOperationsBatchInput) =>
    api<CreateOperationResponse>('/admin/cashbox/operations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateOperation: (id: string, data: UpdateOperationInput) =>
    api<CashboxOperation>(`/admin/cashbox/operations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  softDelete: (id: string) =>
    api<CashboxOperation>(`/admin/cashbox/operations/${id}`, { method: 'DELETE' }),

  restore: (id: string) =>
    api<CashboxOperation>(`/admin/cashbox/operations/${id}/restore`, { method: 'POST' }),

  hardDelete: (id: string) =>
    api<void>(`/admin/cashbox/operations/${id}/permanent`, { method: 'DELETE' }),

  syncAmo: (id: string) =>
    api<CashboxOperation>(`/admin/cashbox/operations/${id}/sync-amo`, { method: 'POST' }),

  // Shifts
  getShifts: (params?: Record<string, string | undefined>) =>
    api<ListResponse<CashboxShift>>(`/admin/cashbox/shifts${buildQuery(params)}`),

  getCurrentShift: () =>
    api<CashboxShift | null>('/admin/cashbox/shifts/current'),

  getShiftExpected: (id: string) =>
    api<{ shiftId: string; openingAmount: number; cashIncome: number; cashExpense: number; expectedAmount: number }>(
      `/admin/cashbox/shifts/${id}/expected`,
    ),

  openShift: (data: { cashboxId?: string; openingAmount: number; comment?: string }) =>
    api<CashboxShift>('/admin/cashbox/shifts/open', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  closeShift: (id: string, data: { closingAmount: number; comment?: string }) =>
    api<CashboxShift>(`/admin/cashbox/shifts/${id}/close`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Archive
  getArchive: (params?: Record<string, string | undefined>) =>
    api<ListResponse<CashboxOperation>>(`/admin/cashbox/archive${buildQuery(params)}`),

  // Import / Export
  importCsv: (formData: FormData, type: OperationType | 'shift' = 'cash') =>
    api<ImportResult>(`/admin/cashbox/import${buildQuery({ type })}`, { method: 'POST', body: formData }),

  exportCsv: (format: 'sheets' | 'flat', filename: string) =>
    downloadFile(`/admin/cashbox/export?format=${format}`, filename),

  // Audit
  getAudit: (params?: Record<string, string | undefined>) =>
    api<ListResponse<CashboxAuditEntry>>(`/admin/cashbox/audit${buildQuery(params)}`),

  // Settings
  getSettings: () =>
    api<CashboxSettings>('/admin/cashbox/settings'),

  updateSettings: (data: Partial<Omit<CashboxSettings, 'id'>>) =>
    api<CashboxSettings>('/admin/cashbox/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // Rates
  getRates: () =>
    api<ListResponse<CashboxRate>>('/admin/cashbox/rates').then((res) => res.items),

  refreshRates: () =>
    api<CashboxRate[]>('/admin/cashbox/rates/refresh', { method: 'POST' }),

  // Reports
  getDashboard: (params?: Record<string, string | undefined>) =>
    api<DashboardApiData>(`/admin/cashbox/reports/dashboard${buildQuery(params)}`),

  sendDailyReport: (params?: Record<string, string | undefined>) =>
    api<{ status: 'sent' | 'skipped' }>(
      `/admin/cashbox/reports/daily/send${buildQuery(params)}`,
      { method: 'POST' },
    ),

  getGroupedReport: (
    by: 'day' | 'week' | 'type' | 'cashier' | 'currency',
    params?: Record<string, string | undefined>,
  ) =>
    api<GroupedReportApiData>(
      `/admin/cashbox/reports/grouped${buildQuery({ by, ...params })}`,
    ).then((res) => res.items),
};
